import React, { useEffect, useState } from "react";
import moment from "moment";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  LuAlertCircle,
  LuCalendar,
  LuFileText,
  LuIndianRupee,
} from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import Button from "../ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/Dialog";
import Input from "../ui/Input";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  dialogInputClass,
  dialogMobileBodyClass,
  dialogMobileContentClass,
  dialogMobileFooterClass,
  dialogMobileFormClass,
  dialogMobileHeaderClass,
  dialogTitleClass,
  fieldErrorIconClass,
  fieldErrorTextClass,
  fieldLabelClass,
  formFieldIconClass,
  formSelectIconClass,
} from "../ui/styles";
import {
  getEditSalary,
  setEditSalary,
  toggleCreateModal,
} from "../../features/salaries/salariesSlice";
import { IFormInputPayment } from "../../types/salaries";

type Props = {
  status: number;
  handleSavePayment: (data: IFormInputPayment) => void;
};

const PaymentForm = ({ status, handleSavePayment }: Props) => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const editSalary = useAppSelector(getEditSalary);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const label = status === 2 ? "ઉપાડ" : "પગાર";

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<IFormInputPayment>({
    defaultValues: {
      amount: null,
      note: "",
      date: moment().format("YYYY-MM-DDTHH:mm"),
    },
  });

  const handleOnSubmit: SubmitHandler<IFormInputPayment> = async (
    data: any
  ) => {
    setIsLoading(true);
    const payload = {
      ...data,
      date: moment(data.date).format("YYYY-MM-DD HH:mm:ss"),
    };

    if (editSalary && editSalary.id) {
      await handleSavePayment({ ...payload, id: editSalary.id, status });
    } else {
      await handleSavePayment({ ...payload, status, employee_id: id });
    }
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleCreateModal(0));
    dispatch(setEditSalary(null));
  };

  useEffect(() => {
    if (editSalary && editSalary.id) {
      reset({
        amount: editSalary.amount,
        note: editSalary.note || "",
        date: moment(editSalary.date || editSalary.created_at).format(
          "YYYY-MM-DDTHH:mm"
        ),
      });
    } else {
      reset({
        amount: null,
        note: "",
        date: moment().format("YYYY-MM-DDTHH:mm"),
      });
    }
  }, [editSalary]);

  return (
    <Dialog open onClose={handleCloseFormModal}>
      <DialogContent
        size="md"
        className={cx("max-h-[90vh] overflow-y-auto", dialogMobileContentClass)}
      >
        <DialogHeader className={dialogMobileHeaderClass}>
          <DialogTitle className={dialogTitleClass}>
            {label} ની માહિતી
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(handleOnSubmit)}
          className={cx("space-y-4", dialogMobileFormClass)}
        >
          <div className={cx("space-y-4", dialogMobileBodyClass)}>
            <div className="space-y-2">
              <label className={fieldLabelClass}>
                તારીખ અને સમય <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <LuCalendar className={formSelectIconClass} />
                <Input
                  type="datetime-local"
                  className={dialogInputClass(!!errors.date)}
                  {...register("date", { required: true })}
                />
                {errors.date && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>

              {errors.date && (
                <p className={fieldErrorTextClass}>તારીખ અને સમય દાખલ કરો</p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>
                {label} નું અમાઉન્ટ <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <LuIndianRupee className={formFieldIconClass} />
                <Input
                  type="number"
                  step="any"
                  placeholder={`${label} નું અમાઉન્ટ દાખલ કરો`}
                  className={dialogInputClass(!!errors.amount)}
                  {...register("amount", { required: true, min: 0 })}
                />
                {errors.amount && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>

              {errors.amount && (
                <p className={fieldErrorTextClass}>
                  {label} નું અમાઉન્ટ દાખલ કરો
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>{label} ની નોંધ</label>

              <div className="relative">
                <LuFileText className={formFieldIconClass} />
                <Input
                  placeholder={`${label} ની નોંધ દાખલ કરો`}
                  className={dialogInputClass()}
                  {...register("note")}
                />
              </div>
            </div>
          </div>

          <DialogFooter className={dialogMobileFooterClass}>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleCloseFormModal}
              disabled={isLoading}
            >
              બંધ કરો
            </Button>

            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={isLoading}
            >
              {isLoading && <span className={buttonSpinnerClass} />}
              આગળ વધો
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentForm;
